import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Clock, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const WARN_MS = 4 * 60 * 1000;
const PORTAL_RE = /^\/(teamportal|clientportal|admin)/;

// Small bottom-right prompt: "session ends in 3:59 — save your work, then re-verify to stay signed in"
export const SessionCountdown = () => {
  const { user, checkAuth } = useAuth();
  const [renewing, setRenewing] = useState(false);
  const { pathname } = useLocation();
  const [left, setLeft] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(null);
  const endsAt = user?.session_ends_at ? new Date(user.session_ends_at).getTime() : null;
  useEffect(() => {
    if (!endsAt) { setLeft(null); return; }
    const tick = () => setLeft(endsAt - Date.now());
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [endsAt]);
  if (!endsAt || left === null || left > WARN_MS || !PORTAL_RE.test(pathname) || dismissedFor === endsAt) return null;
  const secs = Math.max(0, Math.floor(left / 1000));
  const mm = String(Math.floor(secs / 60)).padStart(1, "0"), ss = String(secs % 60).padStart(2, "0");
  const stay = async () => {
    setRenewing(true);
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/renew`, { method: "POST", credentials: "include", headers: { "X-Requested-With": "XMLHttpRequest" } }).catch(() => null);
    setRenewing(false);
    if (r?.ok) { await checkAuth(); return; }
    renew();
  };
  const renew = () => { try { sessionStorage.setItem("gf_after_login", pathname + window.location.search); } catch { /* ignore */ } window.location.href = window.gfPath("/login?renew=1"); };
  return (
    <div role="status" data-testid="session-countdown" className={`fixed bottom-5 right-5 z-[70] w-[320px] border bg-gf-surface/95 backdrop-blur-md p-4 shadow-2xl ${secs < 60 ? "border-red-400/60" : "border-amber-400/50"}`}>
      <div className="flex items-start gap-3">
        <Clock className={`w-5 h-5 shrink-0 ${secs < 60 ? "text-red-300" : "text-amber-300"}`} />
        <div className="min-w-0">
          <p className="font-heading font-bold text-gf-text text-sm">Session ends in <span className="font-mono tabular-nums" data-testid="session-countdown-time">{mm}:{ss}</span></p>
          <p className="font-body text-xs text-gf-muted mt-1">{user?.renewable ? "This device is trusted (verified by e-mail code), so you can extend with one tap." : "For your security every session lasts 30 minutes. Save your work, then re-verify (Google, OTP or PIN) to keep going."}</p>
          <div className="flex gap-2 mt-3">
            {user?.renewable
              ? <button onClick={stay} disabled={renewing} data-testid="session-countdown-stay" className="font-body text-xs font-bold bg-gf-primary text-gf-bg px-3 py-1.5 hover:bg-gf-primary-hover disabled:opacity-60">{renewing ? "Extending…" : "Stay signed in"}</button>
              : <button onClick={renew} data-testid="session-countdown-renew" className="font-body text-xs font-bold bg-gf-primary text-gf-bg px-3 py-1.5 hover:bg-gf-primary-hover">Re-verify now</button>}
            <button onClick={() => setDismissedFor(endsAt)} data-testid="session-countdown-dismiss" className="font-body text-xs text-gf-muted px-3 py-1.5 hover:text-gf-text">Dismiss</button>
          </div>
        </div>
        <button onClick={() => setDismissedFor(endsAt)} aria-label="Dismiss" className="text-gf-muted hover:text-gf-text"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};
